import dayjs from 'dayjs';
import { forwardRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Area, AreaChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useGetWorkflows } from '../../../../../../api/workflows-client/workflows.client.hooks';
import { ContentPlaceholder } from '../../../../../../components/content-placeholder/content-placeholder';
import { useStatusLabels } from '../../../../../../contexts/status-labels.context';
import { formatMonthDate } from '../../../../../../helpers/date-time-helpers';
import {
  createSubGroupColorMap,
  extractPhaseKeys,
  getOrderedStatusesFromWorkflows,
  sortPhaseKeysByWorkflow,
} from '../../../../../../helpers/subgroup-helpers/subgroup-helpers';
import { useGlobalStore } from '../../../../../../store/global-store/global-store';
import { useFlowByPhaseTabStore } from '../../../../../../store/strategy-store/strategy-store';
import {
  setFlowByPhaseSelectedSubGroups,
  showEpicRelatedTasks,
  toggleFlowByPhaseSubGroup,
} from '../../../../../../store/strategy-store/strategy-store.actions';
import { skyLight } from '../../../../../../styles/design-tokens';
import { Metric } from '../../../../strategy.type';
import { LineChartDot } from '../../../components/line-chart/line-chart-dot';
import { hasArrayData } from '../../../initiative-performance.helpers';
import { useContributingBoards } from '../../../initiative-performance.hooks';
import { createSubGroupPhaseMetadata } from '../../flow-by-phase-tab.helpers';
import { useChartData, useFilteredChartData } from '../../flow-by-phase-tab.hooks';
import type { Phase } from '../../flow-by-phase-tab.type';
import { FlowByPhaseChartLegend } from './flow-by-phase-chart-legend';
import { FlowByPhaseChartTooltip } from './flow-by-phase-chart-tooltip';

/**
 * Stacked area chart of task or point counts per phase over the initiative's
 * date range, in either grouped (Bloomfilter phase) or sub-group (source
 * status) mode.
 *
 * Responsibilities:
 * 1. Read chart data, metric, and sub-group selection from the Flow-by-Phase store.
 * 2. In sub-group mode, derive process order by merging the workflows of every
 *    board contributing to the initiative, then assign colors and display
 *    labels per raw status key — ids stay raw so they keep matching the series
 *    and the color map, while labels carry each board's source casing.
 * 3. Seed the sub-group selection when the view is first switched to sub-groups,
 *    keyed on the id set so asynchronously arriving labels or a re-ordering
 *    workflows response do not reset the user's legend clicks.
 * 4. Render the areas in reverse so the end of the process is the bottom band,
 *    matching the Work Periods phase-distribution and dashboard
 *    cumulative-flow charts, and remount the chart when the series set changes
 *    because recharts fixes stack order at registration.
 *
 * Forwards its ref to the chart container so the parent can export the chart
 * as an image.
 *
 * @returns {JSX.Element} The area chart with legend, or a placeholder when there is no data
 */
export const FlowByPhaseChart = forwardRef<HTMLDivElement>((_, ref) => {
  const { t } = useTranslation();
  const metric = useFlowByPhaseTabStore((state) => state.metric);
  const selectedEpic = useFlowByPhaseTabStore((state) => state.selectedEpic || undefined);
  const useGroups = useFlowByPhaseTabStore((state) => state.useGroups);
  const selectedSubGroups = useFlowByPhaseTabStore((state) => state.selectedSubGroups);
  const data = useFlowByPhaseTabStore((state) => state.data);

  const { chartData, currentDate, phaseMetadata } = useChartData();
  const { getStatusLabel } = useStatusLabels();
  const organization = useGlobalStore((state) => state.organization);
  // Fetched unconditionally even though only sub-group mode consumes it. Grouped
  // mode is the default on every load, so gating on `!useGroups` would start the
  // request on the user's first toggle and leave the stack in raw discovery
  // order until it resolved — a visible reorder and recolour mid-view. Prefetching
  // here keeps the toggle instant. Called with one argument to match
  // `work-phase-distribution-chart.tsx`, so both charts share one cache entry.
  const { data: workflows } = useGetWorkflows(organization?.id || null);

  // `useContributingBoards` rebuilds its array every render, so downstream memos
  // key on the joined ids rather than the array identity.
  const contributingBoards = useContributingBoards();
  const contributingBoardIdKey = contributingBoards.map((board) => board.id).join('|');

  // Sub-group ids are raw source statuses, and `extractPhaseKeys` yields them in
  // the API response's JSON key-insertion order, which is not process order.
  // Work Periods can sort by a single workflow because it is scoped to one
  // board; an initiative spans many, so merge every workflow that governs a
  // contributing board — bucketed by step so the merged list still reads
  // backlog-to-done.
  //
  // Deliberately returns nothing rather than falling back to every org workflow
  // when scoping cannot be established (workflows still loading, no contributing
  // boards resolved, or none of them workflow-mapped — assignment is opt-in in
  // Data Management). Unrelated workflows order these statuses by steps they do
  // not actually sit in, and because a status mapped under different steps by
  // different workflows resolves to the earliest rank, one unrelated board can
  // pull the completed statuses out of the bottom band. An empty order leaves
  // the stack unreversed, which is honest about not knowing.
  const workflowStatusOrder = useMemo(() => {
    if (useGroups || !workflows) {
      return [];
    }
    const boardIds = new Set(contributingBoardIdKey ? contributingBoardIdKey.split('|') : []);
    if (boardIds.size === 0) {
      return [];
    }
    return getOrderedStatusesFromWorkflows(
      workflows.filter((workflow) => workflow.subprojects?.some((id) => boardIds.has(id))),
    );
  }, [useGroups, workflows, contributingBoardIdKey]);

  // Create color map for subgroups - sorted first so colors follow process order.
  // `createSubGroupColorMap` cycles color families across successive keys, so it
  // only produces contrasting adjacent bands when its input order matches the
  // display order. The cost is that sub-group colors change once if the
  // workflows response lands after the chart has already rendered; accepted
  // because that window needs an org switch while sub-group mode is already
  // open (the request starts on the chart's first render, and grouped mode is
  // the default on every load).
  const subGroupColorMap = useMemo(() => {
    if (useGroups || !data) {
      return undefined;
    }
    // Extract phase keys from ALL data points, not just the first one
    // This ensures subgroups with no data on the first date still get colors assigned
    const phaseKeys = sortPhaseKeysByWorkflow(extractPhaseKeys(data), workflowStatusOrder);
    return createSubGroupColorMap(phaseKeys);
  }, [useGroups, data, workflowStatusOrder]);

  // Create subgroup phase metadata
  const subGroupPhaseMetadata = useMemo(() => {
    if (useGroups || !subGroupColorMap || !data) {
      return [];
    }
    return createSubGroupPhaseMetadata(data, subGroupColorMap, getStatusLabel, workflowStatusOrder);
  }, [useGroups, data, subGroupColorMap, getStatusLabel, workflowStatusOrder]);

  // Keyed on the sub-group id SET rather than the metadata objects: labels
  // resolve asynchronously as each contributing board's statuses arrive, and on
  // a 20-board initiative that would otherwise re-select every sub-group once
  // per response, undoing the user's legend clicks. Sorted before stringifying
  // so a late `workflows` response, which only re-orders the same ids, does not
  // re-fire the effect for the same reason through a different door.
  // JSON rather than a delimiter join: ids are raw source status names, which
  // may legitimately contain any character including a separator.
  const subGroupIdKey = useMemo(
    () => JSON.stringify(subGroupPhaseMetadata.map((item) => item.id).sort()),
    [subGroupPhaseMetadata],
  );

  // Initialize all sub-groups as selected when switching to sub-groups view
  useEffect(() => {
    if (useGroups) {
      return;
    }
    const subGroupIds = JSON.parse(subGroupIdKey) as string[];
    if (subGroupIds.length > 0) {
      setFlowByPhaseSelectedSubGroups(new Set(subGroupIds));
    }
  }, [useGroups, subGroupIdKey]);

  // Use appropriate metadata based on toggle state
  const allPhaseMetadata = useGroups ? phaseMetadata : subGroupPhaseMetadata;

  // Recharts stacks the first Area added at the bottom, so iterating
  // `allPhaseMetadata` directly would leave the end of the process on top.
  // Render a reversed copy instead, so `Done` (grouped) or the done-step
  // statuses (sub-group) form the bottom band, matching the Work Periods
  // phase-distribution and dashboard cumulative-flow charts.
  //
  // In sub-group mode the reversal is only meaningful once `workflowStatusOrder`
  // has sorted those ids into process order. While the workflows request is in
  // flight — or if it failed — the ids are still raw API discovery order, and
  // reversing that would relocate an arbitrary choice and push the completed
  // statuses to the top. Leave the order alone in that window instead.
  //
  // The copy matters: `allPhaseMetadata` is memoized and shared with the
  // gradient defs and legend, which stay in process order.
  const isOrderKnown = useGroups || workflowStatusOrder.length > 0;
  const stackOrder = useMemo(
    () => (isOrderKnown ? [...(allPhaseMetadata ?? [])].reverse() : (allPhaseMetadata ?? [])),
    [allPhaseMetadata, isOrderKnown],
  );

  // Tooltip rows read top band -> bottom band, the same direction Work Periods
  // presents them. Derived from `stackOrder` rather than re-using
  // `allPhaseMetadata` so the two cannot drift: the first Area is always the
  // bottom band, so the reverse of the render order is always visual top-down.
  const tooltipOrder = useMemo(() => [...stackOrder].reverse(), [stackOrder]);

  // Recharts fixes a stack's order at the moment each <Area> registers, so
  // re-ordering the JSX children of a mounted chart is a no-op and a partially
  // changed series set leaves survivors in their old slots with new series
  // appended on top. Switching epics inside React Query's 300s `staleTime`
  // resolves synchronously, so `data` never goes null and the subtree is never
  // unmounted by the placeholder branch below. Keying the chart on the rendered
  // series forces a remount whenever that set changes, which is the only way
  // the order is actually re-applied. `reverseStackOrder` does not help here:
  // it reverses registration order, which has the same staleness.
  const seriesKey = useMemo(() => JSON.stringify(stackOrder.map((phase) => phase.id)), [stackOrder]);

  // Filter chart data based on visibility (groups or subgroups)
  const filteredChartData = useFilteredChartData(chartData);

  // Handle subgroup toggle
  const handleSubGroupToggle = (id: string, checked: boolean) => {
    toggleFlowByPhaseSubGroup(id, checked);
  };

  // Gradient ids are positional rather than derived from the status name. A
  // name-derived id has to strip every character SVG will not take, which is
  // lossy: statuses differing only in punctuation ("1a.New" vs "1a-New", or a
  // hyphen vs an em-dash) collapse onto one id, both <linearGradient>s claim it,
  // and every band resolves `url(#...)` to whichever came first — so two bands
  // share a fill while their strokes and legend swatches, which read
  // `phase.color` directly, stay distinct. Keyed by phase id so the <defs> pass
  // and the reversed Area pass agree on which gradient belongs to which band.
  const gradientIdByPhaseId = useMemo(
    () => new Map((allPhaseMetadata ?? []).map((phase, index) => [phase.id, `color-${index}`])),
    [allPhaseMetadata],
  );

  if (!currentDate || !hasArrayData(chartData)) {
    return <ContentPlaceholder icon="mobiledata_off" text={t('common.labels.no_data')} />;
  }

  return (
    <div ref={ref} style={{ width: '100%', height: '100%', minHeight: 160 }}>
      <ResponsiveContainer width="100%" height="100%" minHeight={160}>
        <AreaChart key={seriesKey} data={filteredChartData} margin={{ top: 20 }}>
          <defs>
            {allPhaseMetadata?.map((phase) => {
              const gradientId = gradientIdByPhaseId.get(phase.id);
              return (
                <linearGradient key={gradientId} id={gradientId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="20%" stopColor={phase.color} stopOpacity={0.6} />
                  <stop offset="80%" stopColor={phase.color} stopOpacity={0.3} />
                </linearGradient>
              );
            })}
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke={skyLight} />
          <XAxis
            dataKey="date"
            interval="preserveStartEnd"
            tick={{ fontSize: 12 }}
            axisLine={{ stroke: skyLight }}
            tickLine={{ stroke: skyLight }}
            tickFormatter={(date: string) => formatMonthDate(date)}
            padding={{ right: 10 }}
          />
          <YAxis
            tick={{ fontSize: 12 }}
            axisLine={{ stroke: skyLight }}
            tickLine={{ stroke: skyLight }}
            label={{
              value: metric === Metric.Tasks ? t('common.work_units') : t('common.estimation'),
              angle: -90,
              position: 'insideLeft',
              style: { textAnchor: 'middle' },
              offset: 10,
            }}
          />
          <Tooltip content={<FlowByPhaseChartTooltip phaseMetadata={tooltipOrder} metric={metric} />} />
          {stackOrder.map((phase) => {
            const dataKey = metric === Metric.Tasks ? `tasks_${phase.id}` : `points_${phase.id}`;

            return (
              <Area
                key={phase.id}
                type="monotone"
                dataKey={dataKey}
                stackId="1"
                stroke={phase.color}
                fill={`url(#${gradientIdByPhaseId.get(phase.id)})`}
                // Carries the raw status onto the rendered path so a band's
                // identity never has to be recovered from its fill.
                data-phase-id={phase.id}
                fillOpacity={0.6}
                strokeOpacity={1}
                strokeWidth={1}
                name={phase.label}
                connectNulls
                animationDuration={0}
                activeDot={
                  <LineChartDot
                    color={phase.color}
                    onClick={(_, event) => {
                      // Cast event to the proper type to access payload
                      const { payload } = event as unknown as { payload: { date: string } };

                      showEpicRelatedTasks({
                        epic: selectedEpic,
                        phase: useGroups ? (phase.id as Phase) : null,
                        subgroup: useGroups ? null : phase.id,
                        date: dayjs(payload.date, 'YYYY-MM').toDate(),
                      });
                    }}
                  />
                }
              />
            );
          })}
          <Legend
            content={
              <FlowByPhaseChartLegend
                allPhaseMetadata={allPhaseMetadata}
                useGroups={useGroups}
                selectedSubGroups={selectedSubGroups}
                onSubGroupToggle={handleSubGroupToggle}
              />
            }
            verticalAlign="bottom"
            height={!useGroups && allPhaseMetadata && allPhaseMetadata.length > 10 ? 50 : 36}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
});

FlowByPhaseChart.displayName = 'FlowByPhaseChart';
